import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { registrarPagoCobro } from '../../services/cobranzaService';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { formatoMoneda } from './PanelesCobro';
import InputMonto from '../InputMonto';

const COLOR = colorPrimarioTramite('cobro');
const MEDIOS = ['EFECTIVO', 'TRANSFERENCIA', 'DEPOSITO', 'CHEQUE', 'OTRO'];
const hoyIso = () => new Date().toISOString().slice(0, 10);

/**
 * Registra un pago (o la entrega inicial) contra un cobro. `cuotas` son las cuotas vigentes del
 * plan, para imputar el pago a una en particular si corresponde (opcional: la cancelación
 * anticipada se carga sin cuota asociada, ver CobroService.registrarPago).
 */
export default function ModalRegistrarPago({ open, idCobro, cuotas = [], onClose, onGuardado }) {
    const [form, setForm] = useState({ fecha: hoyIso(), monto: '', medio: 'TRANSFERENCIA', esEntregaInicial: false, idCuota: '', observacion: '' });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ fecha: hoyIso(), monto: '', medio: 'TRANSFERENCIA', esEntregaInicial: false, idCuota: '', observacion: '' });
        }
    }, [open]);

    const handleGuardar = async () => {
        const monto = Number(form.monto);
        if (!monto || monto <= 0) {
            toast.error('Ingresá un monto válido.');
            return;
        }
        setGuardando(true);
        try {
            await registrarPagoCobro(idCobro, {
                fecha: form.fecha,
                monto,
                medio: form.medio,
                esEntregaInicial: form.esEntregaInicial,
                idCuota: form.idCuota || null,
                observacion: form.observacion?.trim() || null,
            });
            toast.success('Pago registrado.');
            onGuardado?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo registrar el pago.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}>
            <Box sx={{ background: `${COLOR}12`, borderBottom: `1px solid ${COLOR}33`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
                    <i className="bi bi-cash-coin me-2" style={{ color: COLOR }}></i>Registrar pago
                </span>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {/* El papel del diálogo tiene overflow hidden: sin scroll propio, lo que no entra en una pantalla baja se corta. */}
            <Box sx={{ p: 3, overflowY: 'auto' }}>
                <div className="mb-3">
                    <label className="form-label fw-semibold">Fecha</label>
                    <input type="date" className="form-control" value={form.fecha} onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
                </div>
                {cuotas.length > 0 && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Imputar a cuota (opcional)</label>
                        <select
                            className="form-select"
                            value={form.idCuota}
                            onChange={(e) => {
                                const idCuota = e.target.value;
                                const cuota = cuotas.find((c) => String(c.id) === idCuota);
                                // Precarga el monto de la cuota elegida; sigue siendo editable después.
                                setForm((f) => ({ ...f, idCuota, monto: cuota ? cuota.monto : f.monto }));
                            }}
                        >
                            <option value="">Sin imputar a una cuota específica</option>
                            {cuotas.map((c) => (
                                <option key={c.id} value={c.id}>Cuota {c.numero} — {formatoMoneda(c.monto)}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="mb-3">
                    <label className="form-label fw-semibold">Monto</label>
                    <InputMonto value={form.monto} onChange={(v) => setForm((f) => ({ ...f, monto: v }))} />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-semibold">Medio</label>
                    <select className="form-select" value={form.medio} onChange={(e) => setForm((f) => ({ ...f, medio: e.target.value }))}>
                        {MEDIOS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="esEntregaInicial" checked={form.esEntregaInicial} onChange={(e) => setForm((f) => ({ ...f, esEntregaInicial: e.target.checked }))} />
                    <label className="form-check-label fw-semibold" htmlFor="esEntregaInicial">Es entrega inicial</label>
                </div>
                <div className="mb-4">
                    <label className="form-label fw-semibold">Observación</label>
                    <textarea className="form-control" rows={2} value={form.observacion} onChange={(e) => setForm((f) => ({ ...f, observacion: e.target.value }))} />
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose} disabled={guardando}>Cancelar</button>
                    <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ background: COLOR, borderColor: COLOR }} onClick={handleGuardar} disabled={guardando}>
                        {guardando ? <span className="spinner-border spinner-border-sm" /> : 'Registrar'}
                    </button>
                </div>
            </Box>
        </Dialog>
    );
}
